// UI kits (Blue Market only): a client's kits, one kit's versions with its preview, and which kit a project's rounds use.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from '../api';
import { useLiveTopics } from '../live';
import { Menu, Modal, ago, useConfirm, useToast } from '../ui';
import type { ClaudeRun, Client, Project, Round } from '../types';
import { Page, useCrumbs } from './Shell';
import { ClaudeRunPanel } from './Spec';

interface KitSummary { id: string; client_id: string; name: string; slug: string; product: { id: string; name: string } | null;
  latest: { number: number; status: 'draft' | 'approved'; built: boolean } | null; approved: number | null; versions: number; working: boolean;
  archived: boolean; created_by: string; updated_at: string }
interface KitVersion { id: string; number: number; label: string; status: 'draft' | 'approved'; source: string; approved_at: string | null; approved_by: string | null;
  created_by: string; created_at: string; built: boolean; stale: boolean; tokens: Record<string, number>; icons: number;
  components: { id: string; title: string; frame: string }[]; notes_html: string }
interface KitData extends Omit<KitSummary, 'versions'> { client: { id: string; name: string }; versions: KitVersion[]; run: ClaudeRun | null;
  can_edit: boolean; can_run: boolean; rounds: { project: { id: string; name: string }; round: number; version: number }[];
  projects: { id: string; name: string; version: number }[] }
interface Source { project: { id: string; name: string }; rounds: number[] }
interface KitList { kits: KitSummary[]; sources: Source[]; products: { id: string; name: string }[]; can_edit: boolean; can_run: boolean }
export interface ProjectKit { current: { id: string; kit_id: string; name: string; version: number; status: string } | null;
  choices: { kit_id: string; name: string; versions: { id: string; number: number; status: string }[] }[] }

const StatusBadge = ({ status }: { status: string }) => <span className={`badge tiny-badge ${status === 'approved' ? 'ok' : ''}`}>{status === 'approved' ? 'Approved' : 'Draft'}</span>;

// ---------- a client's kits ----------

export function KitsTab({ client }: { client: Client }) {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['kits', client.id], queryFn: () => get<KitList>(`/api/clients/${client.id}/kits`) });
  const [creating, setCreating] = useState(false);
  if (!data) return <span className="spin muted" />;
  const live = data.kits.filter((k) => !k.archived), archived = data.kits.filter((k) => k.archived);
  const row = (k: KitSummary) => (
    <button key={k.id} className="contact-row" onClick={() => navigate(`/kits/${k.id}`)}>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="row wrap" style={{ gap: 6 }}><b className="small">{k.name}</b>
          {k.latest && <><span className="muted tiny">v{k.latest.number}</span><StatusBadge status={k.latest.status} /></>}
          {k.working && <span className="badge info tiny-badge"><span className="spin" /> Claude is working</span>}</span>
        <span className="muted tiny">{[k.product ? k.product.name : 'Client-wide', k.approved ? `approved up to v${k.approved}` : null, `updated ${ago(k.updated_at)}`].filter(Boolean).join(' · ')}</span>
      </span>
    </button>
  );
  return (
    <section className="card">
      <div className="card-head"><h2>UI kits</h2><span className="grow" />
        {data.can_edit && data.can_run && <button className="btn sm" onClick={() => setCreating(true)} disabled={!data.sources.length}
                                                  title={data.sources.length ? '' : 'A kit starts from a built design round'}>New UI kit</button>}</div>
      {live.map(row)}
      {archived.length > 0 && <div className="list-label">Archived</div>}
      {archived.map(row)}
      {!data.kits.length && <div className="empty small">No UI kits yet. {data.sources.length ? `Claude can make one from a design round: the colours, type, components and icons ${client.name}'s designs use, versioned so new rounds build on it.` : 'A kit starts from a built design round.'}</div>}
      {creating && <NewKitDialog client={client} data={data} onClose={() => setCreating(false)} onMade={(id) => navigate(`/kits/${id}`)} />}
    </section>
  );
}

function RoundPicker({ sources, value, onChange }: { sources: Source[]; value: string; onChange: (v: string) => void }) {
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} required>
      <option value="">Choose a round…</option>
      {sources.map((s) => <optgroup key={s.project.id} label={s.project.name}>{s.rounds.map((n) => <option key={n} value={`${s.project.id}:${n}`}>{s.project.name} v{n}</option>)}</optgroup>)}
    </select>
  );
}
const parseRound = (v: string) => { const [project_id, n] = v.split(':'); return { project_id, round: Number(n) }; };

function NewKitDialog({ client, data, onClose, onMade }: { client: Client; data: KitList; onClose: () => void; onMade: (id: string) => void }) {
  const toast = useToast();
  const latest = data.sources[0] ? `${data.sources[0].project.id}:${data.sources[0].rounds[0]}` : '';
  const [f, setF] = useState({ name: client.name, product_id: '', source: latest, instructions: '' });
  const make = useMutation({
    mutationFn: () => post<KitSummary>(`/api/clients/${client.id}/kits`, { name: f.name, product_id: f.product_id || null, source: parseRound(f.source), instructions: f.instructions }),
    onSuccess: (k) => onMade(k.id), onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title="New UI kit" onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); make.mutate(); }}>
        <label className="field"><span>Name</span><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={200} required autoFocus /></label>
        {data.products.length > 0 && <label className="field"><span>For</span>
          <select className="input" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })}>
            <option value="">All of {client.name}</option>{data.products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>}
        <label className="field"><span>Start from</span><RoundPicker sources={data.sources} value={f.source} onChange={(v) => setF({ ...f, source: v })} />
          <span className="hint">Claude extracts the round's colours, type, spacing, components and icons, keeping its class names so the round looks exactly the same on the kit.</span></label>
        <label className="field"><span>Anything else? (optional)</span><textarea className="input" rows={2} value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} maxLength={4000} placeholder="e.g. include the admin table styles" /></label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!f.name.trim() || !f.source || make.isPending}>{make.isPending && <span className="spin" />}Make the kit</button></div>
      </form>
    </Modal>
  );
}

// ---------- one kit ----------

export function KitPage() {
  const { id = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setCrumbs = useCrumbs();
  const { confirm, dialog } = useConfirm();
  useLiveTopics('workspace');
  const { data: kit, error } = useQuery({ queryKey: ['kit', id], queryFn: () => get<KitData>(`/api/kits/${id}`), retry: false });
  const [revising, setRevising] = useState(false);
  const [renaming, setRenaming] = useState(false);
  useEffect(() => { if (kit) setCrumbs([{ label: 'Clients', to: '/clients' }, { label: kit.client.name, to: `/clients/${kit.client.id}?tab=kits` }, { label: kit.name }]); }, [kit, setCrumbs]);
  if (error) return <Page title="Not found"><p className="muted">This UI kit doesn't exist.</p></Page>;
  if (!kit) return <main className="page"><span className="spin muted" /></main>;
  const n = Number(search.get('v')) || kit.versions[0]?.number;
  const v = kit.versions.find((x) => x.number === n) ?? kit.versions[0];
  const set = (k: KitData) => qc.setQueryData(['kit', id], k);
  const act = (fn: () => Promise<KitData>) => fn().then(set).catch((e: Error) => toast(e.message, true));
  const running = kit.run && (kit.run.status === 'queued' || kit.run.status === 'running');
  const latest = kit.versions[0];
  const usedBy = kit.rounds.filter((r) => r.version === v?.number), projectsOn = kit.projects.filter((p) => p.version === v?.number);
  const removeVersion = async () => {
    if (!(await confirm(`Delete ${v.label}?`, { body: kit.versions.length === 1 ? 'It is the only version, so the whole kit goes.' : 'Its files are removed. Earlier versions stay.', ok: 'Delete', danger: true }))) return;
    try {
      const r = await del<{ kit_deleted: boolean }>(`/api/kits/${id}/versions/${v.number}`);
      if (r.kit_deleted) navigate(`/clients/${kit.client.id}?tab=kits`); else { setSearch({}); qc.invalidateQueries({ queryKey: ['kit', id] }); }
    } catch (e) { toast((e as Error).message, true); }
  };
  const menu = kit.can_edit ? [
    { label: 'Rename…', onClick: () => setRenaming(true) },
    ...(v && v.number === latest.number && v.status === 'draft' && !running ? [{ label: `Delete ${v.label}`, onClick: removeVersion, danger: true }] : []),
    { label: kit.archived ? 'Restore' : 'Archive', onClick: () => act(() => patch(`/api/kits/${id}`, { archived: !kit.archived })) },
  ] : [];
  return (
    <Page title={kit.name} eyebrow={kit.product ? `UI kit · ${kit.product.name}` : 'UI kit'} actions={<>
      {kit.archived && <span className="badge warn">Archived</span>}
      {kit.can_edit && !running && latest?.built && <button className="btn" onClick={() => setRevising(true)}>New version…</button>}
      {menu.length > 0 && <Menu label="More" items={menu} />}
    </>}>
      {kit.run && (running || kit.run.status === 'failed') &&
        <div style={{ marginBottom: 14 }}><ClaudeRunPanel run={kit.run} canStop={kit.can_run} /></div>}
      <div className="row wrap kit-versions" style={{ gap: 6, marginBottom: 12 }}>
        {kit.versions.map((x) => (
          <button key={x.number} className={`chip ${x.number === v?.number ? 'on' : ''}`} onClick={() => setSearch(x.number === latest.number ? {} : { v: String(x.number) })}>
            {x.label}{x.status === 'approved' && ' ✓'}
          </button>
        ))}
      </div>
      {v && <>
        <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
          <StatusBadge status={v.status} />
          <span className="muted small grow">{v.source}{v.approved_at ? ` · approved by ${v.approved_by} ${ago(v.approved_at)}` : ` · ${v.created_by} ${ago(v.created_at)}`}</span>
          {kit.can_edit && v.built && v.stale && <button className="btn sm ghost" onClick={() => act(() => post(`/api/kits/${id}/versions/${v.number}/build`, {}))} title="The files changed since the preview was built">Rebuild preview</button>}
          {kit.can_edit && v.built && !(running && v.number === latest.number) && (v.status === 'draft'
            ? <button className="btn sm primary" onClick={async () => { if (await confirm(`Approve ${v.label}?`, { body: 'Approved versions are the ones to build on, and are never changed again: changes make a new version.', ok: 'Approve' })) act(() => patch(`/api/kits/${id}/versions/${v.number}`, { status: 'approved' })); }}>Approve</button>
            : <button className="btn sm ghost" onClick={() => act(() => patch(`/api/kits/${id}/versions/${v.number}`, { status: 'draft' }))}>Back to draft</button>)}
        </div>
        {v.built ? (
          <div className="kit-layout">
            <iframe className="kit-preview card" title={`${kit.name} ${v.label} preview`} src={`/files/kits/${id}/v${v.number}/preview.html?b=${encodeURIComponent(v.created_at)}`} />
            <aside className="stack" style={{ gap: 12 }}>
              <section className="card pad stack" style={{ gap: 6 }}>
                <h2>In {v.label}</h2>
                <div className="small">{Object.values(v.tokens).reduce((a, b) => a + b, 0)} tokens · {v.icons} icons · {v.components.length} components</div>
                <a className="link-btn small" href={`/files/kits/${id}/v${v.number}/preview.html`} target="_blank" rel="noopener">Open the preview in a new tab</a>
              </section>
              <section className="card pad stack" style={{ gap: 6 }}>
                <h2>Used by</h2>
                {projectsOn.map((p) => <div key={p.id} className="small"><Link to={`/projects/${p.id}?tab=designs`}>{p.name}</Link> <span className="muted">· new rounds</span></div>)}
                {usedBy.map((r) => <div key={`${r.project.id}-${r.round}`} className="small"><Link to={`/projects/${r.project.id}/designs/${r.round}`}>{r.project.name} v{r.round}</Link></div>)}
                {!projectsOn.length && !usedBy.length && <span className="muted small">Nothing yet. Choose this version under a project's Designs tab.</span>}
              </section>
              {v.number === latest.number && kit.run?.status === 'ok' && kit.run.summary_html && (
                <details className="card pad kit-summary"><summary className="small"><b>What Claude did</b></summary>
                  <div className="prose small" dangerouslySetInnerHTML={{ __html: kit.run.summary_html }} /></details>)}
              {v.notes_html && <details className="card pad kit-summary"><summary className="small"><b>Notes</b></summary><div className="prose small" dangerouslySetInnerHTML={{ __html: v.notes_html }} /></details>}
            </aside>
          </div>
        ) : <div className="card pad"><p className="muted small" style={{ margin: 0 }}>{running ? 'Claude is making this version. The preview appears here when it has finished.' : 'Not built yet.'}</p></div>}
      </>}
      {revising && <NewVersionDialog kit={kit} onClose={() => setRevising(false)} onDone={(k) => { set(k); setRevising(false); setSearch({}); }} />}
      {renaming && <RenameDialog kit={kit} onClose={() => setRenaming(false)} onDone={(k) => { set(k); setRenaming(false); }} />}
      {dialog}
    </Page>
  );
}

function NewVersionDialog({ kit, onClose, onDone }: { kit: KitData; onClose: () => void; onDone: (k: KitData) => void }) {
  const toast = useToast();
  const { data: list } = useQuery({ queryKey: ['kits', kit.client_id], queryFn: () => get<KitList>(`/api/clients/${kit.client_id}/kits`) });
  const [mode, setMode] = useState<'claude' | 'copy'>(kit.can_run ? 'claude' : 'copy');
  const [instructions, setInstructions] = useState('');
  const [fold, setFold] = useState('');
  const next = kit.versions[0].number + 1;
  const make = useMutation({
    mutationFn: () => post<KitData>(`/api/kits/${kit.id}/versions`, mode === 'copy' ? { copy_only: true } : { instructions, fold_round: fold ? parseRound(fold) : null }),
    onSuccess: onDone, onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`Make v${next}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); make.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>v{next} starts as a copy of v{next - 1}, which stays as it is, so rounds using it don't change.</p>
        <div className="seg">{kit.can_run && <button type="button" className={mode === 'claude' ? 'on' : ''} onClick={() => setMode('claude')}>Claude changes it</button>}
          <button type="button" className={mode === 'copy' ? 'on' : ''} onClick={() => setMode('copy')}>Copy to edit by hand</button></div>
        {mode === 'claude' ? <>
          <label className="field"><span>What should change?</span><textarea className="input" rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000} autoFocus
            placeholder="e.g. a darker purple for links, larger tap targets on buttons, add a date picker" /></label>
          <label className="field"><span>Fold in a round's styles (optional)</span>
            <select className="input" value={fold} onChange={(e) => setFold(e.target.value)}>
              <option value="">No</option>
              {(list?.sources ?? []).map((s) => <optgroup key={s.project.id} label={s.project.name}>{s.rounds.map((r) => <option key={r} value={`${s.project.id}:${r}`}>{s.project.name} v{r}</option>)}</optgroup>)}
            </select>
            <span className="hint">Generic styles and icons a later round added (its "Kit suggestions") move into the kit.</span></label>
        </> : <p className="muted small" style={{ margin: 0 }}>Edit the files in <span className="mono">clients/…/kits/{kit.slug}/v{next}/</span>, then use Rebuild preview here.</p>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={make.isPending || (mode === 'claude' && !instructions.trim() && !fold)}>{make.isPending && <span className="spin" />}Make v{next}</button></div>
      </form>
    </Modal>
  );
}

function RenameDialog({ kit, onClose, onDone }: { kit: KitData; onClose: () => void; onDone: (k: KitData) => void }) {
  const toast = useToast();
  const [name, setName] = useState(kit.name);
  return (
    <Modal title="Rename the kit" onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); patch<KitData>(`/api/kits/${kit.id}`, { name }).then(onDone).catch((er: Error) => toast(er.message, true)); }}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus />
        <p className="muted tiny" style={{ margin: 0 }}>The folder stays <span className="mono">{kit.slug}</span>, so rounds using the kit keep working.</p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!name.trim()}>Save</button></div>
      </form>
    </Modal>
  );
}

// ---------- a project's kit, and a round's ----------

/** "New rounds use: <kit version>" on a project's Designs tab. Hidden while the client has no kits. */
export function ProjectKitBar({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['project-kit', project.id], queryFn: () => get<ProjectKit>(`/api/projects/${project.id}/kit`) });
  if (!data || (!data.choices.length && !data.current)) return null;
  const change = async (value: string) => {
    try { qc.setQueryData(['project-kit', project.id], await put<ProjectKit>(`/api/projects/${project.id}/kit`, { kit_version_id: value || null })); }
    catch (e) { toast((e as Error).message, true); }
  };
  return (
    <div className="kit-bar small">
      <span className="muted">New rounds use</span>
      {canEdit ? (
        <select className="input compact" value={data.current?.id ?? ''} onChange={(e) => change(e.target.value)} aria-label="UI kit for new rounds">
          <option value="">No UI kit</option>
          {data.choices.map((k) => <optgroup key={k.kit_id} label={k.name}>{k.versions.map((v) => <option key={v.id} value={v.id}>{k.name} v{v.number}{v.status === 'approved' ? '' : ' (draft)'}</option>)}</optgroup>)}
        </select>
      ) : <b>{data.current ? `${data.current.name} v${data.current.version}` : 'no UI kit'}</b>}
      {data.current && <Link to={`/kits/${data.current.kit_id}?v=${data.current.version}`} className="tiny">See the kit</Link>}
    </div>
  );
}

/** Move a round that was never shared to a kit version (or off kits); it's rebuilt straight away. */
export function RoundKitDialog({ project, round, onClose }: { project: Project; round: Round; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['project-kit', project.id], queryFn: () => get<ProjectKit>(`/api/projects/${project.id}/kit`) });
  const [value, setValue] = useState(round.kit ? `${round.kit.kit_id}:${round.kit.version}` : '');
  const save = useMutation({
    mutationFn: () => { const [kit_id, version] = value.split(':'); return put(`/api/projects/${project.id}/rounds/${round.number}/kit`, value ? { kit_id, version: Number(version) } : { kit_id: null }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rounds', project.id] }); toast(`Rebuilding ${round.label}…`); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title={`UI kit for ${round.label}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <select className="input" value={value} onChange={(e) => setValue(e.target.value)} autoFocus>
          <option value="">No UI kit (the round's own styles only)</option>
          {(data?.choices ?? []).map((k) => <optgroup key={k.kit_id} label={k.name}>{k.versions.map((v) => <option key={v.id} value={`${k.kit_id}:${v.number}`}>{k.name} v{v.number}{v.status === 'approved' ? '' : ' (draft)'}</option>)}</optgroup>)}
        </select>
        <p className="muted small" style={{ margin: 0 }}>The kit's styles load first and {round.label}'s own styles after them, then the round is rebuilt. Look at the screens afterwards. Only rounds that haven't been shared can change.</p>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={save.isPending}>{save.isPending && <span className="spin" />}Apply and rebuild</button></div>
      </form>
    </Modal>
  );
}
